import { Model } from "@vuex-orm/core";
import { DateTime } from "luxon";

import { UploadedDocumentAttachments } from "../document/document-model";
import { clientEntity } from "./client-entity";
import { technicianEntity } from "./technician-entity";
import { getBrowserIanaTimeZone as localTz } from "../../helper/browser-time-zone";
import PrototypeInstance from "../../helper/prototype-instance";

export interface documentEntity {
  documentId: string;
  title: string;
  alternateName: string;
  isFavorite: boolean;
  isArchive: boolean;
  contentId: string;
  parentDocumentId: string | null;
  createdById: number;
  createdOn: string;
  updatedById: number;
  updatedOn: string;
  lastViewedDate: string;
  clientId: number;
  document: string | null;
  documentOrder: number;
  showChildDocuments: boolean;
  contentLoaded: boolean;
  attachments: UploadedDocumentAttachments[];
  isPublic: boolean;
}

export class documentEntity extends Model {
  static entity = "documentEntity";
  static primaryKey = "documentId";
  static fields() {
    return {
      documentId: this.attr(null),
      title: this.string(""),
      alternateName: this.string(""),
      isFavorite: this.boolean(false),
      isArchive: this.boolean(false),
      contentId: this.string(""),
      parentDocumentId: this.attr(null),
      createdById: this.number(0),
      createdOn: this.attr(null),
      updatedById: this.number(0),
      updatedOn: this.attr(null),
      lastViewedDate: this.attr(null),
      clientId: this.number(0),
      document: this.attr(null),
      documentOrder: this.number(0),
      showChildDocuments: this.boolean(false),
      contentLoaded: this.boolean(false),
      attachments: this.attr([]),
      isPublic: this.boolean(false),
    };
  }

  static afterUpdate(_record: any) {}

  get childDocuments() {
    return documentEntity
      .query<any>()
      .where("parentDocumentId", this.documentId)
      .where("isArchive", false)
      .orderBy("documentOrder", "asc")
      .all();
  }

  get createdByAndCreatedOn() {
    let createdByTechnician = technicianEntity
      .query()
      .where("technicianId", this.createdById)
      .first();
    if (createdByTechnician != null) {
      return `${createdByTechnician.fullName} created ${this.createdOnLabel}`;
    }
    return "";
  }

  get createdByAndLastViewed() {
    let createdByTechnician = technicianEntity
      .query()
      .where("technicianId", this.createdById)
      .first();
    if (createdByTechnician != null) {
      return `${createdByTechnician.fullName} last viewed ${this.lastViewedLabel}`;
    }
    return "";
  }

  get createdByAndUpdatedOn() {
    let createdByTechnician = technicianEntity
      .query()
      .where("technicianId", this.createdById)
      .first();
    if (createdByTechnician != null) {
      return `${createdByTechnician.fullName} updated ${this.updatedOnLabel}`;
    }
    return "";
  }

  get isBeforeSevenDays() {
    let createdOn = DateTime.fromISO(this.createdOn, {
      zone: "utc",
    }).setZone(localTz());
    let currentDate = DateTime.utc().setZone(
      localTz(),
    );
    let diff = currentDate.diff(createdOn, ["days", "hours"]);

    return diff.days >= 7;
  }

  get createdOnLabel() {
    let createdOn = DateTime.fromISO(this.createdOn, {
      zone: "utc",
    }).setZone(localTz());

    if (this.isBeforeSevenDays) {
      let convertedTime = createdOn
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
      return "on " + convertedTime;
    } else {
      let currentDate = DateTime.utc().setZone(
        localTz(),
      );
      let yesterdayDate = DateTime.utc()
        .setZone(localTz())
        .minus({ days: 1 })
        .toISODate();
      if (createdOn.toISODate() == currentDate.toISODate()) {
        return "Today";
      } else if (createdOn.toISODate() == yesterdayDate) {
        return "Yesterday";
      }

      let dayOfTheWeek: string = createdOn
        .setLocale(navigator.language)
        .toFormat("EEEE");
      return "on " + dayOfTheWeek;
    }
  }

  get lastViewedLabel() {
    let createdOn = DateTime.fromISO(this.lastViewedDate, {
      zone: "utc",
    }).setZone(localTz());

    if (this.isBeforeSevenDays) {
      let convertedTime = createdOn
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
      return "on " + convertedTime;
    } else {
      let currentDate = DateTime.utc().setZone(
        localTz(),
      );
      let yesterdayDate = DateTime.utc()
        .setZone(localTz())
        .minus({ days: 1 })
        .toISODate();
      if (createdOn.toISODate() == currentDate.toISODate()) {
        return "Today";
      } else if (createdOn.toISODate() == yesterdayDate) {
        return "Yesterday";
      }

      let dayOfTheWeek: string = createdOn
        .setLocale(navigator.language)
        .toFormat("EEEE");
      return "on " + dayOfTheWeek;
    }
  }

  get updatedOnLabel() {
    let createdOn = DateTime.fromISO(this.updatedOn, {
      zone: "utc",
    }).setZone(localTz());

    if (this.isBeforeSevenDays) {
      let convertedTime = createdOn
        .setLocale(navigator.language)
        .toLocaleString(DateTime.DATE_FULL);
      return "on " + convertedTime;
    } else {
      let currentDate = DateTime.utc().setZone(
        localTz(),
      );
      let yesterdayDate = DateTime.utc()
        .setZone(localTz())
        .minus({ days: 1 })
        .toISODate();
      if (createdOn.toISODate() == currentDate.toISODate()) {
        return "Today";
      } else if (createdOn.toISODate() == yesterdayDate) {
        return "Yesterday";
      }

      let dayOfTheWeek: string = createdOn
        .setLocale(navigator.language)
        .toFormat("EEEE");
      return "on " + dayOfTheWeek;
    }
  }

  get serviceProviderName(): string {
    let serviceProviderInfo: any =
      PrototypeInstance.globalProperties.$storeInstance!.getters
        .getServiceProviderInfo;
    if (serviceProviderInfo != null) {
      return serviceProviderInfo.serviceProviderName;
    }

    return "MSP";
  }

  get clientName(): string {
    let client = clientEntity.find(this.clientId);
    if (client) return client.clientName;

    return "";
  }

  get directory(): any {
    let baseDirectory: string = `${this.serviceProviderName} / `;

    if (this.clientId > 0) {
      let client = clientEntity.find(this.clientId);
      if (client != null) {
        baseDirectory = client.clientName + " / ";
      }
    }

    //If document does not have a parent
    if (this.parentDocumentId == "" || this.parentDocumentId == null) {
      return {
        title: baseDirectory + this.title,
        documentIds: [this.documentId],
      };
    } else {
      let startingNodeFound: boolean = false;
      let directories: any = {
        title: this.title as string,
        documentIds: [this.documentId] as string[],
      };
      let parentDocumentId: string = this.parentDocumentId;

      while (!startingNodeFound) {
        let parentDocument = documentEntity
          .query<any>()
          .where("documentId", parentDocumentId)
          .first();
        if (parentDocument != null) {
          directories.title = parentDocument.title + " / " + directories.title;
          directories.documentIds.push(parentDocument.documentId);
          if (
            parentDocument.parentDocumentId == "" ||
            parentDocument.parentDocumentId == null
          ) {
            startingNodeFound = true;
            if (parentDocument.clientId > 0) {
              let client = clientEntity
                .query<any>()
                .where("clientId", parentDocument.clientId)
                .first();
              if (client != null) {
                baseDirectory = client.clientName + " / ";
              }
            }
          } else {
            parentDocumentId = parentDocument.parentDocumentId;
          }
        } else {
          startingNodeFound = true;
        }
      }
      directories.title = baseDirectory + directories.title;
      return directories;
    }
  }
}
